import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Wifi, Users, Key, LifeBuoy, AlertTriangle, ArrowLeft, Smartphone, Cable } from 'lucide-react';
import { customerAPI } from '../../services/api';

// Customer self-service WiFi page — shows the current SSID(s) from TR-069/ACS for the
// customer's own linked router and lets them push a new name/password. If their router
// isn't linked to our TR-069 system (or ACS can't be reached), this shows a "Get Support"
// fallback instead of a broken/empty settings form.
export default function CustomerWifiSettings() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null); // { linked, online, unreachable, wifi }
  const [loading, setLoading] = useState(true);
  const [band, setBand] = useState('2.4');
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    customerAPI.getMyWifi()
      .then(({ data }) => {
        setStatus(data.data);
        const bands = (data.data?.wifi || []).map(w => w.band);
        if (bands.length) setBand(bands.includes('2.4') ? '2.4' : bands[0]);
      })
      .catch(() => toast.error('Failed to load WiFi settings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ssid && !password) { toast.error('Enter a new WiFi name or password'); return; }
    setSaving(true);
    try {
      const { data } = await customerAPI.updateMyWifi({ band, ssid: ssid || undefined, password: password || undefined });
      toast.success(data.data?.completed ? 'WiFi updated!' : 'Change queued — applies when your router next connects');
      setSsid(''); setPassword('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'WiFi change failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      <button onClick={() => navigate(-1)} style={backBtn}>
        <ArrowLeft size={16} /> Back
      </button>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4 }}>Wi-Fi Settings</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 20 }}>View and update your router's WiFi</p>

      {(!status?.linked || status?.unreachable) ? (
        <div className="card" style={{ padding: '24px 20px', textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: '#fef3c7', display: 'flex',
            alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
          }}>
            <AlertTriangle size={22} color="#d97706" />
          </div>
          <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 6 }}>
            {status?.unreachable ? "Can't reach your router right now" : 'Not set up for remote WiFi management yet'}
          </div>
          <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginBottom: 18 }}>
            {status?.unreachable
              ? 'Your router is linked but not responding at the moment — it may be offline or restarting. Please try again shortly, or get support.'
              : "We haven't linked your router to our remote management system, so we can't show or change your WiFi settings from here. Our team can help you set this up or change your WiFi directly."}
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/portal/complaints?new=1')} style={{ margin: '0 auto' }}>
            <LifeBuoy size={14} style={{ marginRight: 6 }} /> Get Support
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
            {(status.wifi || []).length === 0 && (
              <div className="card empty-state" style={{ padding: 32 }}>
                <Wifi size={28} />
                <p>No WiFi networks reported yet.</p>
              </div>
            )}
            {(status.wifi || []).map((w, i) => (
              <div key={i} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: w.enabled ? '#dcfce7' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Wifi size={18} color={w.enabled ? '#16a34a' : '#9ca3af'} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.92rem' }}>{w.ssid || <em style={{ color: 'var(--text-muted)' }}>(no SSID)</em>}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{w.band} GHz · {w.enabled ? 'Enabled' : 'Disabled'}</div>
                </div>
                {w.clients != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.78rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                    <Users size={12} /> {w.clients}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
            Connected Devices {status.devices?.length > 0 && `(${status.devices.length})`}
          </div>
          {(!status.devices || status.devices.length === 0) ? (
            <div className="card empty-state" style={{ padding: 28, marginBottom: 20 }}>
              <Users size={26} />
              <p>No devices connected right now.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
              {status.devices.map((d, i) => {
                const Icon = d.interface === 'wifi' ? Smartphone : Cable;
                return (
                  <div key={i} className="card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: 'var(--primary-light)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={16} color="var(--primary)" />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.hostname || <em style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Unknown device</em>}
                      </div>
                      <div className="font-mono" style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{d.ip} · {d.mac}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="card">
            <div className="card-header"><h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.95rem' }}><Key size={16} color="var(--primary)" /> Change WiFi Name / Password</h3></div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {(status.wifi || []).length > 1 && (
                  <div className="form-group">
                    <label className="form-label">Band</label>
                    <select className="form-control" value={band} onChange={e => setBand(e.target.value)}>
                      {status.wifi.map(w => <option key={w.band} value={w.band}>{w.band} GHz</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">New WiFi Name (SSID) <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                  <input className="form-control" value={ssid} onChange={e => setSsid(e.target.value)} placeholder="Leave blank to keep current" maxLength={32} />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional, 8-63 chars)</span></label>
                  <input className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
                </div>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: '100%', justifyContent: 'center' }}>
                  {saving ? 'Applying…' : 'Apply Changes'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const backBtn = {
  display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-muted)', fontWeight: 600, fontSize: '.85rem', padding: 0, marginBottom: 14,
};
